import { Form, Formik } from "formik";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RichTextField } from "@/components/common/rich-text-field";
import { sendESignature } from "@/features/esignatures/api";
import { textToHtml } from "@/lib/rich-text";
import type {
  RecipientInput,
  RecipientRole,
  SendESignPayload,
} from "@/features/esignatures/types";
import { getUsageReport } from "@/features/esignatures/api";
import { useAuthStore } from "@/features/auth/store/auth-store";
import type { Client } from "@/features/clients/types";
import { getFirmSettings } from "@/features/settings/firm-api";
import { ApiError } from "@/lib/api";

interface RoleOption {
  role: RecipientRole;
  label: string;
  name: string;
  email: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client;
  /** The stored document being sent (id + display name). */
  document: { id: string; name: string } | null;
  /** Called after a successful send so the caller can refresh its list. */
  onSent?: () => void;
}

/** Derive the selectable recipient roles from the client profile. */
function roleOptions(client: Client): RoleOption[] {
  if (client.type === "Business") {
    const contact = [client.businessContactFirstName, client.businessContactLastName]
      .filter(Boolean)
      .join(" ");
    return [
      {
        role: "BUSINESS",
        label: "Business contact",
        name: contact || client.businessName || client.name,
        email: client.email ?? "",
      },
    ];
  }
  const options: RoleOption[] = [
    {
      role: "TAXPAYER",
      label: "Taxpayer",
      name: client.name,
      email: client.email ?? "",
    },
  ];
  if (client.spouse) {
    options.push({
      role: "SPOUSE",
      label: "Spouse",
      name: client.spouse.name ?? "",
      // The spouse email isn't on the summary Client object — the server
      // resolves it from the profile and enforces the phase-0 gate.
      email: "",
    });
  }
  return options;
}

function parseEmails(raw: string): string[] {
  return raw
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);
}

interface FormValues {
  emailSubject: string;
  emailBody: string;
  signByDate: string;
  cc: string;
  moreBcc: string;
  bccMe: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const schema = Yup.object({
  emailSubject: Yup.string().trim().required("Subject is required"),
  emailBody: Yup.string().trim().required("Email body is required"),
  signByDate: Yup.string().test(
    "not-past",
    "The sign-by date can't be in the past",
    (v) => !v || v >= new Date().toISOString().slice(0, 10),
  ),
  cc: Yup.string().test("emails", "One or more CC emails are invalid", (v) =>
    !v ? true : parseEmails(v).every((e) => EMAIL_RE.test(e)),
  ),
  moreBcc: Yup.string().test("emails", "One or more BCC emails are invalid", (v) =>
    !v ? true : parseEmails(v).every((e) => EMAIL_RE.test(e)),
  ),
});

export function SendESignDialog({ open, onOpenChange, client, document, onSent }: Props) {
  const userEmail = useAuthStore((s) => s.user?.email ?? "");
  const options = roleOptions(client);
  const [selectedRoles, setSelectedRoles] = useState<RecipientRole[]>([]);
  const [phase, setPhase] = useState<"compose" | "sent">("compose");
  const [blockingFields, setBlockingFields] = useState<string[]>([]);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [quota, setQuota] = useState<number | null>(null);
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhase("compose");
    setBlockingFields([]);
    setSelectedRoles(options.length === 1 && options[0] ? [options[0].role] : ["TAXPAYER"]);
    setReady(false);
    Promise.all([getFirmSettings(), getUsageReport()])
      .then(([settings, report]) => {
        setTemplateSubject(settings.esignEmailSubject);
        setTemplateBody(settings.esignEmailBody);
        setRemaining(report.remaining);
        setQuota(report.quota);
      })
      .catch(() => {
        /* non-blocking: the compose form still works without prefill/quota */
      })
      .finally(() => setReady(true));
    // options is derived from client; client identity is stable per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client.id]);

  const toggleRole = (role: RecipientRole) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  // Light client-side phase-0 pre-check. Only flags fields we actually hold on
  // the summary Client object (names, and the taxpayer/business email); the
  // server enforces the authoritative gate (spouse email/SSN, EIN) and its
  // `profile_incomplete` response drives the same MissingInfoPhase view.
  const clientSideMissing = (): string[] => {
    const missing: string[] = [];
    for (const opt of options) {
      if (!selectedRoles.includes(opt.role)) continue;
      if (!opt.name.trim()) missing.push(`${opt.label} name`);
      if (opt.role !== "SPOUSE" && !opt.email.trim()) missing.push(`${opt.label} email`);
    }
    return missing;
  };

  const initialValues: FormValues = {
    emailSubject: templateSubject || `Please sign: ${document?.name ?? ""}`,
    // textToHtml upgrades a legacy plain-text firm template for the editor.
    emailBody:
      textToHtml(templateBody) ||
      "<p>Your tax professional has prepared a document that needs your " +
        "signature. Please review and sign using the secure link below. You " +
        "will be asked to confirm the last four digits of your SSN before " +
        "signing.</p><p>{link}</p>",
    signByDate: "",
    cc: "",
    moreBcc: "",
    bccMe: false,
  };

  const quotaBlocked = remaining !== null && remaining <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {phase === "sent" ? (
          <SentConfirmation onClose={() => onOpenChange(false)} />
        ) : blockingFields.length > 0 ? (
          <MissingInfoPhase
            client={client}
            fields={blockingFields}
            onBack={() => setBlockingFields([])}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Send for e-signature</DialogTitle>
              <DialogDescription>
                {document ? document.name : "Select a document"} — the recipient
                receives a secure link and is asked for the last four of their SSN
                before signing.
              </DialogDescription>
            </DialogHeader>

            {ready && (
              <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={schema}
                onSubmit={async (values, helpers) => {
                  if (!document) return;
                  const missing = clientSideMissing();
                  if (missing.length > 0) {
                    setBlockingFields(missing);
                    helpers.setSubmitting(false);
                    return;
                  }
                  const recipients: RecipientInput[] = selectedRoles.map((role) => ({
                    role,
                  }));
                  const payload: SendESignPayload = {
                    document: document.id,
                    recipients,
                    email_subject: values.emailSubject.trim(),
                    email_body: values.emailBody.trim(),
                    sign_by_date: values.signByDate || null,
                    cc: parseEmails(values.cc),
                    bcc_me: values.bccMe,
                    more_bcc: parseEmails(values.moreBcc),
                  };
                  try {
                    await sendESignature(payload);
                    setPhase("sent");
                    onSent?.();
                  } catch (err) {
                    if (err instanceof ApiError && err.data && typeof err.data === "object") {
                      const errors = (err.data as { errors?: Record<string, unknown> })
                        .errors;
                      const pi = errors?.profile_incomplete;
                      if (Array.isArray(pi)) {
                        setBlockingFields(pi as string[]);
                        return;
                      }
                    }
                    toast.error(
                      err instanceof ApiError ? err.message : "Couldn't send the request.",
                    );
                  } finally {
                    helpers.setSubmitting(false);
                  }
                }}
              >
                {({ values, setFieldValue, isSubmitting }) => (
                  <Form className="grid gap-4">
                    {/* Recipients */}
                    <div className="grid gap-2">
                      <Label>Recipients</Label>
                      <div className="grid gap-2">
                        {options.map((opt) => (
                          <label
                            key={opt.role}
                            className="hover:bg-accent flex items-start gap-3 rounded-lg border p-3 text-sm"
                          >
                            <Checkbox
                              checked={selectedRoles.includes(opt.role)}
                              onCheckedChange={() => toggleRole(opt.role)}
                              disabled={options.length === 1}
                              className="mt-0.5"
                            />
                            <span>
                              <span className="text-foreground font-medium">
                                {opt.label}
                              </span>
                              <span className="text-muted-foreground block text-xs">
                                {opt.name || "(no name on file)"}
                                {" · "}
                                {opt.email || "profile email on file"}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                      {selectedRoles.length === 0 && (
                        <p className="text-destructive text-sm">Select at least one recipient.</p>
                      )}
                    </div>

                    <FormField name="emailSubject" label="Email subject" />

                    <BodyField />


                    <FormField name="cc" label="CC (comma-separated emails)" />
                    <FormField name="moreBcc" label="BCC (comma-separated emails)" />
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={values.bccMe}
                        onCheckedChange={(c) => setFieldValue("bccMe", c === true)}
                      />
                      BCC me{userEmail ? ` (${userEmail})` : ""}
                    </label>

                    <FormField name="signByDate" label="Sign-by date (optional)" type="date" />

                    {remaining !== null && (
                      <p className="text-muted-foreground text-sm">
                        {remaining} of {quota} signature request
                        {quota === 1 ? "" : "s"} remaining
                        {!quotaBlocked && `, ${remaining - 1} after sending`}.
                      </p>
                    )}
                    {quotaBlocked && (
                      <p className="text-destructive flex items-center gap-1.5 text-sm">
                        <AlertTriangle className="size-4" />
                        No requests remaining. Purchase more signature packs to
                        continue.
                      </p>
                    )}

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={
                          isSubmitting ||
                          quotaBlocked ||
                          selectedRoles.length === 0 ||
                          !document
                        }
                      >
                        {isSubmitting ? "Sending…" : "Send"}
                      </Button>
                    </DialogFooter>
                  </Form>
                )}
              </Formik>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Formik-bound rich email body with consistent error rendering. */
function BodyField() {
  return (
    <RichTextField
      name="emailBody"
      label="Email body"
      minHeight={140}
      hint={
        <>
          <code>{"{link}"}</code> is replaced with the recipient's signing link.
        </>
      }
    />
  );
}

function MissingInfoPhase({
  client,
  fields,
  onBack,
}: {
  client: Client;
  fields: string[];
  onBack: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="text-warning size-5" /> Missing client information
        </DialogTitle>
        <DialogDescription>
          Before sending, complete these fields on the client profile:
        </DialogDescription>
      </DialogHeader>
      <ul className="list-inside list-disc text-sm">
        {fields.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button asChild>
          <Link to={`/client/${client.id}/profile`}>Edit profile</Link>
        </Button>
      </DialogFooter>
    </>
  );
}

function SentConfirmation({ onClose }: { onClose: () => void }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="text-success size-5" /> Signature request sent
        </DialogTitle>
        <DialogDescription>
          The recipient(s) will receive an email invitation with a secure signing
          link. You can track progress from the E-Signatures tab.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button onClick={onClose}>Done</Button>
      </DialogFooter>
    </>
  );
}
